import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatFormFieldModule, FloatLabelType } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, debounceTime } from 'rxjs';

import { isEmailValid } from '@smartcharge/shared/utils/validators/email';

import { PipesModule } from '../../pipes/pipes.module';
import { UserType } from '../../user-roles';
import { DirectivesModule } from '../../directives/directives.module';
import { UserService } from '../../lib';
import { CustomWS, WebsocketService } from '../../services/websocket/websocket.service';
import { WebsocketSearchUser } from '@smartcharge/shared/index';

export type UserSearchResult = WebsocketSearchUser | { Email: string; isCreateEmailOption: boolean };

@UntilDestroy()
@Component({
  selector: 'sc-user-search-input',
  standalone: true,
  imports: [
    CommonModule,
    DirectivesModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    PipesModule,
    TranslateModule
  ],
  templateUrl: './user-search.component.html',
  styleUrls: ['./user-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserSearchInputComponent),
      multi: true
    }
  ]
})
export class UserSearchInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  readonly UserType = UserType;

  readonly autocomplete = viewChild.required('autoComplete', { read: MatAutocompleteTrigger });

  readonly acceptCustomValue = input<boolean>(false);
  readonly floatLabel = input<FloatLabelType>('auto');
  readonly label = input<string>();
  readonly minSearchLength = input<number>(2);
  readonly placeholder = input<string>('');
  readonly required = input<boolean>(false);
  readonly styleClass = input<string>('');
  readonly value = input<string>('');

  readonly cleared = output<void>();
  readonly enter = output<string>();
  readonly itemSelected = output<UserSearchResult>();

  readonly isLoading = signal<boolean>(false);
  private readonly searchedUsers = signal<WebsocketSearchUser[]>([]);

  readonly autocompleteOptions = computed<UserSearchResult[]>(() => {
    return this.acceptCustomValue() && this.canCreateCustomValue()
      ? [...this.searchedUsers(), this.createOption]
      : this.searchedUsers();
  });

  readonly searchCtrl = new FormControl<string>(this.value());

  private readonly userService = inject(UserService);
  private readonly ws = inject(WebsocketService);

  private currentSearchText = '';
  private exactMatch = false;
  private searchInited = false;
  private socketRetries = 0;
  private userWS: CustomWS;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  get createOption() {
    return {
      isCreateEmailOption: true,
      Email: this.currentSearchText
    };
  }

  ngOnInit() {
    this.initSocket();

    this.searchCtrl.setValue(this.value(), { emitEvent: false });

    this.searchCtrl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged(), debounceTime(200))
      .subscribe((value) => {
        if (typeof value === 'string') {
          this.currentSearchText = value.trim();

          if (!this.currentSearchText) {
            this.searchedUsers.set([]);
            this.exactMatch = false;
            return;
          }

          if (this.currentSearchText.length < this.minSearchLength()) {
            this.searchedUsers.set([]);
            this.exactMatch = false;
            return;
          }

          this.sendSearch(this.currentSearchText);
        } else {
          this.currentSearchText = this.displayFn(value);
          this.updateExactMatch(this.currentSearchText);
        }
      });
  }

  ngOnDestroy() {
    this.clearSocket();
  }

  clearSearch() {
    this.searchCtrl.reset('');
    this.currentSearchText = '';
    this.exactMatch = false;
    this.searchedUsers.set([]);
    this.onChange(null);
    this.cleared.emit();
  }

  displayFn(user: any): string {
    if (!user) {
      return '';
    }

    if (typeof user === 'string') {
      return user;
    }

    if (user.isCreateEmailOption) {
      return user.Email || '';
    }

    return user.Email || user.Name || '';
  }

  markTouched() {
    this.onTouched();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  selectItem(event: MatAutocompleteSelectedEvent) {
    const item = event.option.value;
    if (!item) {
      return;
    }

    const selection = item.isCreateEmailOption ? { Email: item.Email, CreateUser: true } : item;

    this.searchCtrl.setValue(selection, { emitEvent: false });
    this.onChange(selection);
    this.itemSelected.emit(selection);
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchCtrl.disable({ emitEvent: false });
    } else {
      this.searchCtrl.enable({ emitEvent: false });
    }
  }

  trackUser(user: any) {
    return user?.PK_CustomerID || user?.Email;
  }

  writeValue(value: any): void {
    if (value == null) {
      this.searchCtrl.reset('');

      return;
    }

    this.searchCtrl.setValue(value, { emitEvent: false });
    this.currentSearchText = typeof value === 'string' ? value : this.displayFn(value);
    this.updateExactMatch(this.currentSearchText);
  }

  private canCreateCustomValue() {
    return (
      this.acceptCustomValue() && this.currentSearchText && !this.exactMatch && isEmailValid(this.currentSearchText)
    );
  }

  private clearSocket() {
    if (this.userWS) {
      this.searchInited = false;
      this.userWS.webSocket.complete();
      this.userWS = null;
    }
  }

  private initSocket() {
    if (this.userWS) {
      return;
    }

    this.userService.getUser().subscribe(async (user) => {
      this.userWS = await this.ws.init('Users/SearchUser/' + user.PK_CustomerID);
      this.userWS.webSocket.subscribe({
        next: (res) => {
          this.isLoading.set(false);

          if (res?.List) {
            this.searchedUsers.set(res.List);
          } else if (Array.isArray(res)) {
            this.searchedUsers.set(res);
          }
          this.updateExactMatch(this.currentSearchText);
        },
        error: () => {
          if (this.socketRetries < 3) {
            this.socketRetries++;
            this.initSocket();
          } else {
            this.socketRetries = 0;
          }
        }
      });
      this.userWS.onOpen.then(() => {
        this.searchInited = true;
      });
    });
  }

  private sendSearch(searchText: string) {
    this.isLoading.set(true);

    if (!this.searchInited || !this.userWS) {
      return;
    }

    this.userWS.webSocket.next({ Text: searchText, Nb: 10 });
  }

  private updateExactMatch(searchText: string) {
    const normalized = (searchText || '').trim().toLowerCase();

    if (!normalized) {
      this.exactMatch = false;
      return;
    }

    this.exactMatch = this.searchedUsers().some((user) => {
      const candidate = this.displayFn(user).trim().toLowerCase();

      return candidate === normalized;
    });
  }
}
